import { Suspense, useMemo, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Chip } from '@nextui-org/react';
import {
  AnalystInsightContext,
  PRIVATE_DASHBOARD_ITEM_PREFIX,
  type AnalystInsight,
  type AnalystPageTab,
  type AnalystRuntimeSwitchProps,
} from '@circuitry-ai/doc-analyst';
import { CtySpinner } from '@circuitry-ai/doc-ui';
import { lazyWithRetry, withChunkRecovery } from '../lazyload/lazyload';
import {
  AI_INSIGHT_PATH,
  AI_INSIGHT_TABS,
  ANALYST_AGENT_TAB,
  CUSTOMISE_DEFAULT_URL,
  DEFAULT_AGENT_CODE,
  DEFAULT_AGENT_DESCRIPTION,
  DEFAULT_AGENT_NAME,
  LEGACY_AI_INSIGHT_PATH,
  agentEditorUrl,
  aiInsightUrl,
  dashboardUrl,
  getInsightAgentUrl,
  type AIInsightTab,
} from './insightAgentConfig';
import { usePrivateDashboards, useUnpublishDashboard } from './dashboard/dashboardAnalyst';

/**
 * AI Insight's routes for app.tsx. Both pages load lazily (they pull in the advisors module's
 * MarkdownEditor, which must stay out of the main bundle), with the same retry and
 * reload-on-chunk-failure as every other lazy page.
 */
const AIInsightPageLazy = lazyWithRetry(() => import('./AIInsightPage')
  .then((module) => ({ default: module.AIInsightPage }))
  .catch((error) => withChunkRecovery(error)));
const InsightAgentEditorLazy = lazyWithRetry(() => import('./insightAgents/InsightAgentEditor')
  .then((module) => ({ default: module.InsightAgentEditor }))
  .catch((error) => withChunkRecovery(error)));
const DashboardViewLazy = lazyWithRetry(() => import('./dashboard/DashboardView')
  .then((module) => ({ default: module.DashboardView }))
  .catch((error) => withChunkRecovery(error)));
const AnalystRuntimeSwitchLazy = lazyWithRetry(() => import('./insightAgents/AnalystRuntimeSwitch')
  .then((module) => ({ default: module.AnalystRuntimeSwitch }))
  .catch((error) => withChunkRecovery(error)));

/** The Analyst Agent tab of the Analysts page: /analytics?tab=analyst-agent */
export const AIInsightPage = () => (
  <Suspense fallback={<CtySpinner size="lg" />}>
    <AIInsightPageLazy />
  </Suspense>
);

/** /analytics/analyst-agent/agents/:mode/:aiworker_id? */
export const InsightAgentEditor = () => (
  <Suspense fallback={<CtySpinner size="lg" />}>
    <InsightAgentEditorLazy />
  </Suspense>
);

/** /analytics/analyst-agent/dashboards/:configId */
export const DashboardView = () => (
  <Suspense fallback={<CtySpinner size="lg" />}>
    <DashboardViewLazy />
  </Suspense>
);

/** A decision analyst's runtime switch, on its Configuration tab. */
function AnalystRuntimeSwitch(props: AnalystRuntimeSwitchProps) {
  return (
    <Suspense fallback={<CtySpinner size="sm" />}>
      <AnalystRuntimeSwitchLazy {...props} />
    </Suspense>
  );
}

/** AI Insight as the Analysts page uses it: links into it, the runtime switch, and shared dashboards' pages. */
const ANALYST_INSIGHT: AnalystInsight = {
  conversationUrl: (agentId) => aiInsightUrl(AI_INSIGHT_TABS.CONVERSATION, agentId ? { agent: agentId } : {}),
  editorUrl: (agentId) => agentEditorUrl('edit', agentId),
  customiseDefaultUrl: CUSTOMISE_DEFAULT_URL,
  defaultAgentCode: DEFAULT_AGENT_CODE,
  defaultAgentName: DEFAULT_AGENT_NAME,
  defaultAgentDescription: DEFAULT_AGENT_DESCRIPTION,
  RuntimeSwitch: AnalystRuntimeSwitch,
  dashboardUrl,
};

/**
 * Around the Analysts pages (/analytics and /analytics/:mode), for app.tsx. Where the Insight
 * runtime is configured, All Analysts also lists the Insight agents and the dashboards shared with
 * the tenant (and, for their owner, the viewer's private ones), a decision analyst switched to the
 * Insight runtime is asked there, and its Configuration tab shows the switch. Elsewhere (production
 * builds) the pages are as they were.
 */
export function AnalystInsightProvider({ children }: { readonly children: ReactNode }) {
  const unpublishDashboard = useUnpublishDashboard();
  const configured = Boolean(getInsightAgentUrl());
  const privateDashboards = usePrivateDashboards(configured, PRIVATE_DASHBOARD_ITEM_PREFIX);
  const value = useMemo(
    () => (configured ? { ...ANALYST_INSIGHT, unpublishDashboard, privateDashboards } : null),
    [configured, privateDashboards, unpublishDashboard]
  );
  return (
    <AnalystInsightContext.Provider value={value}>
      {children}
    </AnalystInsightContext.Provider>
  );
}

function BetaChip() {
  const { t } = useTranslation();
  return <Chip size="sm" variant="flat" color="warning">{t('Beta')}</Chip>;
}

const WITH_ANALYST_AGENT: readonly AnalystPageTab[] = [
  { key: ANALYST_AGENT_TAB, title: 'Analyst Agent', endContent: <BetaChip />, fillHeight: true, render: () => <AIInsightPage /> },
];
const WITHOUT_ANALYST_AGENT: readonly AnalystPageTab[] = [];

/**
 * The Analysts page's Analyst Agent tab, for app.tsx (`<Analystpage extraTabs={…} />`). Dev builds
 * only: shown where CTY_INSIGHT_AGENT_URL is set, to anyone who may open the Analysts page.
 */
export function analystAgentTabs(): readonly AnalystPageTab[] {
  return getInsightAgentUrl() ? WITH_ANALYST_AGENT : WITHOUT_ANALYST_AGENT;
}

const SECTIONS = Object.values(AI_INSIGHT_TABS) as string[];

/**
 * Old AI Insight links. /ai-insight?tab=<section>&… opens the Analyst Agent tab on that section;
 * /ai-insight/dashboards/… and /ai-insight/agents/… open the same page under /analytics/analyst-agent.
 */
export function LegacyAIInsightRedirect() {
  const { pathname, search, state } = useLocation();
  const rest = pathname.slice(LEGACY_AI_INSIGHT_PATH.length).replace(/\/+$/, '');
  if (rest) return <Navigate to={`${AI_INSIGHT_PATH}${rest}${search}`} state={state} replace />;
  const params = new URLSearchParams(search);
  const requested = params.get('tab') ?? '';
  params.delete('tab');
  const section = (SECTIONS.includes(requested) ? requested : AI_INSIGHT_TABS.CONVERSATION) as AIInsightTab;
  return <Navigate to={aiInsightUrl(section, Object.fromEntries(params))} state={state} replace />;
}

/**
 * Old bookmarks: the Insight Agent used to be a tab of /data (?tab=insight-agent, and briefly
 * ?tab=insight-agents). Those now open AI Insight; every other /data URL renders `children`.
 */
export function RedirectInsightBookmarks({ children }: { readonly children: ReactElement }) {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  if (tab === 'insight-agent') {
    const agent = searchParams.get('agent');
    return <Navigate to={aiInsightUrl(AI_INSIGHT_TABS.CONVERSATION, agent ? { agent } : {})} replace />;
  }
  if (tab === 'insight-agents') {
    const view = searchParams.get('view');
    return <Navigate to={aiInsightUrl(AI_INSIGHT_TABS.CONFIGURATION, view ? { view } : {})} replace />;
  }
  return children;
}
